import { Flex, Group, Stack } from '@mantine/core';
import {
	Selector,
	SelectorTarget,
	SelectType,
} from '@repo/common/components/Selectors';
import type { PropsWithChildren } from 'react';
import { memo, useMemo } from 'react';
import type { FilterOption } from '../types';

export type MetricsOverviewTabProps = {
	filterTypes: string[];
	setFilterTypes: (filterTypes: string[]) => void;
	filterOptions: FilterOption[][];
	onFilterChange?: (index: number, value: string) => void;
	showAllUsersInTooltip?: boolean;
	setShowAllUsersInTooltip?: (showAll: boolean) => void;
};

const getShowAllUsersItems = async () => [
	{ value: 'false', label: 'Tooltip shows: Top users' },
	{ value: 'true', label: 'Tooltip shows: All users' },
];

export const MetricsOverviewTab = memo(
	({
		filterTypes,
		setFilterTypes,
		filterOptions,
		onFilterChange,
		showAllUsersInTooltip = false,
		setShowAllUsersInTooltip,
		children,
	}: PropsWithChildren<MetricsOverviewTabProps>) => {
		const selectedShowAllUsersLabel = useMemo(
			() =>
				showAllUsersInTooltip
					? 'Tooltip shows: All users'
					: 'Tooltip shows: Top users',
			[showAllUsersInTooltip]
		);

		const handleFilterChange = (index: number, value: string) => {
			const newFilterTypes = [...filterTypes];
			newFilterTypes[index] = value;
			setFilterTypes(newFilterTypes);
			onFilterChange?.(index, value);
		};

		return (
			<Stack spacing={0}>
				<Flex justify="space-between" align="center">
					<Group position="left" spacing="xs">
						{filterTypes.map((type, i) => (
							<Selector
								key={type}
								initialValue={[type]}
								getItems={async () => filterOptions[i]}
								onChange={(values) =>
									handleFilterChange(i, values[0] as string)
								}
								selectType={SelectType.Single}
								disableSearch
							>
								<SelectorTarget size="md" variant="default">
									{
										filterOptions[i].find(
											(item) => item.value === filterTypes[i]
										)?.label
									}
								</SelectorTarget>
							</Selector>
						))}
						{setShowAllUsersInTooltip && (
							<Selector
								initialValue={[String(showAllUsersInTooltip)]}
								getItems={getShowAllUsersItems}
								onChange={(values) =>
									setShowAllUsersInTooltip?.(values[0] === 'true')
								}
								selectType={SelectType.Single}
								disableSearch
							>
								<SelectorTarget size="md" variant="default">
									{selectedShowAllUsersLabel}
								</SelectorTarget>
							</Selector>
						)}
					</Group>
				</Flex>
				{children}
			</Stack>
		);
	}
);
